import { useState, useEffect } from 'react';
import classnames from 'classnames';
import { Panel } from '../Panel';
import Input from '../../../../atoms/Input/Input';
import Dropdown from '../../../../atoms/Dropdown/Dropdown';
import Typography from '../../../../atoms/Typography/Typography';
import Swatch from '../../../../atoms/Swatch/Swatch';
import RadioGroup from '../../../../atoms/RadioGroup/RadioGroup';
import CheckboxGroup from '../../../../atoms/CheckboxGroup/CheckboxGroup';
import Box from '../../../../atoms/Box/Box';
import FilterPills from '../../FilterPills/FilterPills';
import { TableColumn, FilterState, FilterValue, FilterType } from '../../types';
import styles from './Filter.module.scss';

export type FilterProps<T = Record<string, unknown>> = {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback when the panel is closed */
  onClose: () => void;
  /** Columns that can be filtered (must have filter config) */
  columns: TableColumn<T>[];
  /** Current filter state */
  filterState: FilterState;
  /** Callback when filters change */
  onFilterChange: (filters: FilterState) => void;
  /** Theme for the panel */
  theme?: 'light' | 'dark';
  /** Test ID for the panel */
  dataTestId?: string;
  /**
   * Custom filter content to render instead of column-based filters.
   * Use this for complex/custom filter UIs like faceted search.
   */
  filterContent?: React.ReactNode;
};

type FilterInputProps = {
  column: TableColumn;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  theme: 'light' | 'dark';
  dataTestId: string;
};

/**
 * Renders the appropriate filter input based on the filter type
 */
const FilterInput = ({ column, value, onChange, theme, dataTestId }: FilterInputProps) => {
  const filterConfig = column.filter;
  if (!filterConfig) return null;

  const filterType: FilterType = filterConfig.type;

  switch (filterType) {
    case 'text':
      return (
        <Input
          name={`filter-${column.key}`}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={filterConfig.placeholder ?? `Filter by ${column.title}`}
          theme={theme}
          size="medium"
          data-testid={dataTestId}
          showValidation={false}
        />
      );

    case 'number':
      return (
        <Box className={styles.rangeInputs}>
          <Input
            name={`filter-${column.key}-min`}
            type="text"
            value={
              typeof value === 'object' && value !== null && 'from' in value
                ? String(value.from ?? '')
                : ''
            }
            onChange={(e) => {
              const currentValue =
                typeof value === 'object' && value !== null
                  ? value
                  : { from: undefined, to: undefined };
              const newFrom = e.target.value ? Number(e.target.value) : undefined;
              const newTo = 'to' in currentValue ? currentValue.to : undefined;
              if (newFrom === undefined && newTo === undefined) {
                onChange(null);
              } else {
                onChange({ from: newFrom, to: newTo });
              }
            }}
            placeholder={filterConfig.placeholder ?? 'Min'}
            theme={theme}
            size="medium"
            data-testid={`${dataTestId}-min`}
            showValidation={false}
          />
          <Typography variant="body" size="small" color={theme === 'dark' ? 'white' : 'gray-9'}>
            to
          </Typography>
          <Input
            name={`filter-${column.key}-max`}
            type="text"
            value={
              typeof value === 'object' && value !== null && 'to' in value
                ? String(value.to ?? '')
                : ''
            }
            onChange={(e) => {
              const currentValue =
                typeof value === 'object' && value !== null
                  ? value
                  : { from: undefined, to: undefined };
              const newTo = e.target.value ? Number(e.target.value) : undefined;
              const newFrom = 'from' in currentValue ? currentValue.from : undefined;
              if (newFrom === undefined && newTo === undefined) {
                onChange(null);
              } else {
                onChange({ from: newFrom, to: newTo });
              }
            }}
            placeholder="Max"
            theme={theme}
            size="medium"
            data-testid={`${dataTestId}-max`}
            showValidation={false}
          />
        </Box>
      );

    case 'select':
      return (
        <Dropdown
          name={`filter-${column.key}`}
          options={filterConfig.options ?? []}
          selectedOption={(value as string) ?? ''}
          handleSelect={(val) => onChange(val || null)}
          placeholder={filterConfig.placeholder ?? `Select ${column.title}`}
          theme={theme}
          size="medium"
          showValidation={false}
        />
      );

    case 'multiselect':
      return (
        <Dropdown
          name={`filter-${column.key}`}
          options={filterConfig.options ?? []}
          selectedOptions={(value as string[]) ?? []}
          handleMultiSelect={(values) => onChange(values.length > 0 ? values : null)}
          placeholder={filterConfig.placeholder ?? `Select ${column.title}`}
          theme={theme}
          size="medium"
          multiSelect
          showValidation={false}
        />
      );

    case 'date':
      return (
        <Input
          name={`filter-${column.key}`}
          type="text"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={filterConfig.placeholder ?? 'YYYY-MM-DD'}
          theme={theme}
          size="medium"
          data-testid={dataTestId}
          showValidation={false}
        />
      );

    case 'dateRange':
      return (
        <Box className={styles.rangeInputs}>
          <Input
            name={`filter-${column.key}-from`}
            type="text"
            value={
              typeof value === 'object' && value !== null && 'from' in value
                ? String(value.from ?? '')
                : ''
            }
            onChange={(e) => {
              const currentValue =
                typeof value === 'object' && value !== null
                  ? value
                  : { from: undefined, to: undefined };
              const newFrom = e.target.value || undefined;
              const newTo = 'to' in currentValue ? currentValue.to : undefined;
              if (!newFrom && !newTo) {
                onChange(null);
              } else {
                onChange({ from: newFrom, to: newTo });
              }
            }}
            placeholder="From date"
            theme={theme}
            size="medium"
            data-testid={`${dataTestId}-from`}
            showValidation={false}
          />
          <Typography variant="body" size="small" color={theme === 'dark' ? 'white' : 'gray-9'}>
            to
          </Typography>
          <Input
            name={`filter-${column.key}-to`}
            type="text"
            value={
              typeof value === 'object' && value !== null && 'to' in value
                ? String(value.to ?? '')
                : ''
            }
            onChange={(e) => {
              const currentValue =
                typeof value === 'object' && value !== null
                  ? value
                  : { from: undefined, to: undefined };
              const newTo = e.target.value || undefined;
              const newFrom = 'from' in currentValue ? currentValue.from : undefined;
              if (!newFrom && !newTo) {
                onChange(null);
              } else {
                onChange({ from: newFrom, to: newTo });
              }
            }}
            placeholder="To date"
            theme={theme}
            size="medium"
            data-testid={`${dataTestId}-to`}
            showValidation={false}
          />
        </Box>
      );

    case 'swatch':
      return (
        <Box className={styles.swatchList}>
          {filterConfig.options?.map((option) => {
            const selectedValues = (value as string[]) ?? [];
            const isSelected = selectedValues.includes(option.value);

            return (
              <Swatch
                key={option.value}
                theme={theme}
                shape={filterConfig.swatchShape ?? 'circle'}
                size="extraSmall"
                specSize="extraSmall"
                layout="horizontal"
                title={option.label}
                image={option.image}
                active={isSelected}
                clickable
                handleClick={() => {
                  const currentValues = (value as string[]) ?? [];
                  let newValues: string[];
                  if (isSelected) {
                    newValues = currentValues.filter((v) => v !== option.value);
                  } else {
                    newValues = [...currentValues, option.value];
                  }
                  onChange(newValues.length > 0 ? newValues : null);
                }}
                dataTestId={`${dataTestId}-${option.value}`}
              />
            );
          })}
        </Box>
      );

    case 'radio':
      return (
        <RadioGroup
          name={`filter-${column.key}`}
          options={
            filterConfig.options?.map((opt) => ({
              value: opt.value,
              label: opt.label,
            })) ?? []
          }
          selectedValue={(value as string) ?? undefined}
          handleChange={(option) => onChange(option.value || null)}
          theme={theme}
          layout="vertical"
          showValidation={false}
          dataTestId={dataTestId}
        />
      );

    case 'checkbox':
      return (
        <CheckboxGroup
          name={`filter-${column.key}`}
          options={
            filterConfig.options?.map((opt) => ({
              value: opt.value,
              label: opt.label,
            })) ?? []
          }
          selectedValues={(value as string[]) ?? []}
          handleChange={(selectedValues) =>
            onChange(selectedValues.length > 0 ? selectedValues : null)
          }
          theme={theme}
          layout="vertical"
          showValidation={false}
          dataTestId={dataTestId}
        />
      );

    case 'toggle':
      return (
        <Box className={styles.toggleGroup} data-testid={dataTestId}>
          {filterConfig.options?.map((option) => {
            const isSelected = (value as string) === option.value;
            return (
              <button
                key={option.value}
                type="button"
                className={classnames(styles.toggleButton, {
                  [styles.selected]: isSelected,
                  [styles.dark]: theme === 'dark',
                  [styles.light]: theme === 'light',
                })}
                onClick={() => onChange(option.value)}
                data-testid={`${dataTestId}-${option.value}`}
              >
                {option.label}
              </button>
            );
          })}
        </Box>
      );

    default:
      return null;
  }
};

/**
 * Panel component for filtering table data.
 * Generates filter inputs based on column definitions.
 * Displayed inside a Slider component.
 *
 * @component
 * @param {FilterProps} props - Component props
 * @returns {React.ReactElement} The Filter component
 * @example
 * <Filter
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   columns={columns}
 *   filterState={filters}
 *   onFilterChange={setFilters}
 * />
 */
export const Filter = <T extends Record<string, unknown>>({
  isOpen,
  onClose,
  columns,
  filterState,
  onFilterChange,
  theme = 'dark',
  dataTestId = 'filter-panel',
  filterContent,
}: FilterProps<T>) => {
  const [localFilters, setLocalFilters] = useState<FilterState>(filterState);

  // Sync local state when external state changes (using JSON stringify for deep comparison)
  const filterStateKey = JSON.stringify(filterState);
  useEffect(() => {
    setLocalFilters(filterState);
  }, [filterStateKey, filterState]);

  const filterableColumns = columns.filter((col) => col.filter);

  const handleFilterChange = (columnKey: string, value: FilterValue) => {
    setLocalFilters((prev) => ({
      ...prev,
      [columnKey]: value,
    }));
  };

  const handleApply = () => {
    // Remove null/undefined values before applying
    const cleanedFilters: FilterState = {};
    Object.entries(localFilters).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        cleanedFilters[key] = value;
      }
    });
    onFilterChange(cleanedFilters);
    onClose();
  };

  const activeFilterCount = Object.values(localFilters).filter(
    (v) => v !== null && v !== undefined
  ).length;

  // If custom filter content is provided, render it in a simpler panel
  if (filterContent) {
    return (
      <Panel
        isOpen={isOpen}
        onClose={onClose}
        title="Filters"
        theme={theme}
        width="464px"
        dataTestId={dataTestId}
        showFooter={false}
      >
        <Box className={styles.customContent} data-testid={`${dataTestId}-custom-content`}>
          {filterContent}
        </Box>
      </Panel>
    );
  }

  return (
    <Panel
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleApply}
      saveLabel="Apply"
      title="Filters"
      theme={theme}
      width="400px"
      dataTestId={dataTestId}
    >
      <Box className={styles.content} data-testid={`${dataTestId}-content`}>
        {activeFilterCount > 0 && (
          <FilterPills
            filterState={localFilters}
            columns={filterableColumns as TableColumn[]}
            onFilterChange={setLocalFilters}
            theme={theme}
            showClearAll={true}
            dataTestId={`${dataTestId}-pills`}
          />
        )}

        {filterableColumns.length === 0 ? (
          <Box className={styles.emptyState}>
            <Typography variant="body" size="small" color={theme === 'dark' ? 'gray-5' : 'gray-7'}>
              No filterable columns available.
            </Typography>
          </Box>
        ) : (
          <Box className={styles.filterList}>
            {filterableColumns.map((column) => {
              const currentValue = localFilters[column.key];

              return (
                <Box key={column.key} className={styles.filterItem}>
                  <Box className={styles.filterHeader}>
                    <Typography
                      variant="label"
                      size="small"
                      color={theme === 'dark' ? 'white' : 'gray-9'}
                    >
                      {column.title}
                    </Typography>
                  </Box>
                  <FilterInput
                    column={column as TableColumn<Record<string, unknown>>}
                    value={currentValue}
                    onChange={(value) => handleFilterChange(column.key, value)}
                    theme={theme}
                    dataTestId={`${dataTestId}-input-${column.key}`}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Panel>
  );
};

Filter.displayName = 'Filter';
